import React, { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSearch,
  faPlay,
  faPause,
  faVolumeUp,
  faVolumeMute,
  faVolumeDown,
  faCompactDisc,
  faFilter,
  faTimes,
  faExternalLinkAlt,
  faMusic,
} from "@fortawesome/free-solid-svg-icons";
import RecommendationsDrawer from "./RecommendationsDrawer";
import "./Discover.scss";

const API_BASE =
  process.env.REACT_APP_DISCOVERY_API_URL ||
  "https://api.spinninbeans.com";

// ─── Helpers ─────────────────────────────────────────────────
function formatDuration(ms) {
  if (!ms) return "";
  const mins = Math.floor(ms / 60000);
  const secs = Math.floor((ms % 60000) / 1000);
  return `${mins}:${secs.toString().padStart(2, "0")}`;
}

function formatPlays(n) {
  if (!n) return null;
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toString();
}

const GENRES = [
  "House",
  "Techno",
  "Drum & Bass",
  "Trance",
  "Minimal / Deep Tech",
  "Melodic House & Techno",
  "Tech House",
  "Breaks",
  "Electro",
  "Afro House",
  "Indie Dance",
  "Organic House / Downtempo",
  "Progressive House",
  "Hard Techno",
  "UK Garage / Bassline",
];

const CAMELOT_KEYS = [
  "1A","1B","2A","2B","3A","3B","4A","4B","5A","5B","6A","6B",
  "7A","7B","8A","8B","9A","9B","10A","10B","11A","11B","12A","12B",
];

// ─── Track Card ──────────────────────────────────────────────
const TrackCard = ({ track, isPlaying, onPlay, onPause, onFindSimilar }) => {
  const platformColor = {
    beatport: "#94d500",
    soundcloud: "#ff5500",
    bandcamp: "#1da0c3",
  };

  return (
    <div className="track-card" onClick={() => onFindSimilar(track)}>
      <div className="track-artwork">
        {track.primary_image_url ? (
          <img src={track.primary_image_url} alt="" loading="lazy" />
        ) : (
          <div className="artwork-placeholder">
            <FontAwesomeIcon icon={faCompactDisc} />
          </div>
        )}
        <button
          className="play-btn"
          onClick={(e) => {
            e.stopPropagation();
            isPlaying ? onPause() : onPlay(track);
          }}
          aria-label={isPlaying ? "Pause" : "Play"}
        >
          <FontAwesomeIcon icon={isPlaying ? faPause : faPlay} />
        </button>
        {track.platform && (
          <span
            className="platform-badge"
            style={{ backgroundColor: platformColor[track.platform] || "#666" }}
          >
            {track.platform}
          </span>
        )}
      </div>

      <div className="track-info">
        <h3 className="track-title" title={track.primary_title}>
          {track.primary_title || "Unknown Title"}
        </h3>
        <p className="track-artist">{track.artist_norm || "Unknown Artist"}</p>

        <div className="track-meta">
          {track.bpm && <span className="meta-tag">{Math.round(track.bpm)} BPM</span>}
          {track.camelot_key && <span className="meta-tag key">{track.camelot_key}</span>}
          {track.primary_genre && (
            <span className="meta-tag genre">{track.primary_genre}</span>
          )}
          {track.duration_ms && (
            <span className="meta-tag">{formatDuration(track.duration_ms)}</span>
          )}
          {track.total_plays > 0 && track.platform === "soundcloud" && (
            <span className="meta-tag plays">{formatPlays(track.total_plays)} plays</span>
          )}
        </div>
      </div>

      {track.external_url && (
        <a
          href={track.external_url}
          target="_blank"
          rel="noopener noreferrer"
          className="external-link"
          title={`Open on ${track.platform}`}
          onClick={(e) => e.stopPropagation()}
        >
          <FontAwesomeIcon icon={faExternalLinkAlt} />
        </a>
      )}
    </div>
  );
};

// ─── Audio Preview Player (bottom bar) ───────────────────────
const PreviewPlayer = ({ track, isPlaying, onPlayPause, audioRef, onFindSimilar }) => {
  const [progress, setProgress] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(80);
  const [showVolume, setShowVolume] = useState(false);
  const [muted, setMuted] = useState(false);
  const progressRef = useRef(null);
  const volumeRef = useRef(null);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const onTime = () => {
      setCurrentTime(audio.currentTime);
      setDuration(audio.duration || 0);
      setProgress(audio.duration ? (audio.currentTime / audio.duration) * 100 : 0);
    };

    audio.addEventListener("timeupdate", onTime);
    audio.addEventListener("loadedmetadata", onTime);
    return () => {
      audio.removeEventListener("timeupdate", onTime);
      audio.removeEventListener("loadedmetadata", onTime);
    };
  }, [audioRef]);

  // Close volume slider on outside click
  useEffect(() => {
    if (!showVolume) return;
    const handleClick = (e) => {
      if (volumeRef.current && !volumeRef.current.contains(e.target)) {
        setShowVolume(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [showVolume]);

  const handleSeek = (e) => {
    const audio = audioRef.current;
    const bar = progressRef.current;
    if (!audio || !bar || !isFinite(audio.duration)) return;
    const rect = bar.getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    audio.currentTime = pct * audio.duration;
  };

  const handleVolumeChange = (e) => {
    const val = Number(e.target.value);
    setVolume(val);
    setMuted(val === 0);
    if (audioRef.current) {
      audioRef.current.volume = val / 100;
      audioRef.current.muted = false;
    }
  };

  const toggleMute = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (muted) {
      audio.muted = false;
      audio.volume = (volume || 80) / 100;
      setMuted(false);
      if (volume === 0) setVolume(80);
    } else {
      audio.muted = true;
      setMuted(true);
    }
  };

  const volIcon = muted || volume === 0
    ? faVolumeMute
    : volume < 50
      ? faVolumeDown
      : faVolumeUp;

  const fmtTime = (s) => {
    if (!s || isNaN(s)) return "0:00";
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, "0")}`;
  };

  if (!track) return null;

  return (
    <div className="preview-player">
      <div
        className="player-track-info"
        onClick={() => onFindSimilar && onFindSimilar(track)}
        style={{ cursor: "pointer" }}
      >
        {track.primary_image_url ? (
          <img src={track.primary_image_url} alt="" className="player-art" />
        ) : (
          <div className="player-art placeholder">
            <FontAwesomeIcon icon={faMusic} />
          </div>
        )}
        <div className="player-text">
          <span className="player-title">{track.primary_title}</span>
          <span className="player-artist">{track.artist_norm}</span>
        </div>
      </div>

      <div className="player-controls">
        <button className="player-play-btn" onClick={onPlayPause}>
          <FontAwesomeIcon icon={isPlaying ? faPause : faPlay} />
        </button>
        <span className="player-time">{fmtTime(currentTime)}</span>
        <div className="player-progress" ref={progressRef} onClick={handleSeek}>
          <div className="player-progress-fill" style={{ width: `${progress}%` }} />
        </div>
        <span className="player-time">{fmtTime(duration)}</span>

        <div className="volume-control" ref={volumeRef}>
          <button className="volume-btn" onClick={toggleMute} onMouseEnter={() => setShowVolume(true)}>
            <FontAwesomeIcon icon={volIcon} />
          </button>
          {showVolume && (
            <div className="volume-popup">
              <input
                type="range"
                min="0"
                max="100"
                value={muted ? 0 : volume}
                onChange={handleVolumeChange}
                className="volume-slider"
                style={{ '--vol': `${muted ? 0 : volume}%` }}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Main Discover Page ──────────────────────────────────────
const Discover = () => {
  const [query, setQuery] = useState("");
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState("");
  const [total, setTotal] = useState(0);
  const [searchTime, setSearchTime] = useState(null);

  // Filters
  const [showFilters, setShowFilters] = useState(false);
  const [genre, setGenre] = useState("");
  const [bpmMin, setBpmMin] = useState("");
  const [bpmMax, setBpmMax] = useState("");
  const [camelotKey, setCamelotKey] = useState("");

  // Audio player
  const [currentTrack, setCurrentTrack] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);

  // Recommendations drawer
  const [recTrack, setRecTrack] = useState(null);
  const audioRef = useRef(new Audio());

  // Debounced search
  const searchTimeoutRef = useRef(null);

  const doSearch = useCallback(async (params = {}) => {
    setLoading(true);
    const qs = new URLSearchParams();
    if (params.q) qs.set("q", params.q);
    if (params.genre) qs.set("genre", params.genre);
    if (params.bpm_min) qs.set("bpm_min", params.bpm_min);
    if (params.bpm_max) qs.set("bpm_max", params.bpm_max);
    if (params.key) qs.set("key", params.key);
    // In browse mode (no query/filters), request curated Spinnin' Beans tracks
    if (!params.q && !params.genre && !params.bpm_min && !params.bpm_max && !params.key) {
      qs.set("curator", "spinnin-beans");
    }
    qs.set("limit", "30");

    try {
      const res = await fetch(`${API_BASE}/api/search?${qs}`);
      if (!res.ok) throw new Error(`${res.status}`);
      const data = await res.json();
      setTracks(data.tracks || []);
      setMode(data.mode || "");
      setTotal(data.total || 0);
      setSearchTime(data.processing_time_ms);
    } catch (err) {
      console.error("Search failed:", err);
      setTracks([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial browse load
  useEffect(() => {
    doSearch({});
  }, [doSearch]);

  // Text search with debounce
  const handleQueryChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      doSearch({
        q: val,
        genre,
        bpm_min: bpmMin,
        bpm_max: bpmMax,
        key: camelotKey,
      });
    }, 400);
  };

  const handleFilterApply = () => {
    doSearch({
      q: query,
      genre,
      bpm_min: bpmMin,
      bpm_max: bpmMax,
      key: camelotKey,
    });
  };

  const clearFilters = () => {
    setGenre("");
    setBpmMin("");
    setBpmMax("");
    setCamelotKey("");
    doSearch({ q: query });
  };

  // Audio playback
  const playTrack = (track) => {
    const audio = audioRef.current;
    if (!track.preview_url) return;

    // Build the full URL for proxy endpoints
    let url = track.preview_url;
    if (url.startsWith("/api/")) {
      url = `${API_BASE}${url}`;
    }

    if (currentTrack?.id === track.id && isPlaying) {
      audio.pause();
      setIsPlaying(false);
      return;
    }

    audio.src = url;
    audio.play().catch(() => {});
    setCurrentTrack(track);
    setIsPlaying(true);
  };

  const togglePlayPause = () => {
    const audio = audioRef.current;
    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
    } else {
      audio.play().catch(() => {});
      setIsPlaying(true);
    }
  };

  // Cleanup audio on unmount
  useEffect(() => {
    const audio = audioRef.current;
    const onEnded = () => setIsPlaying(false);
    audio.addEventListener("ended", onEnded);
    return () => {
      audio.removeEventListener("ended", onEnded);
      audio.pause();
      audio.src = "";
    };
  }, []);

  const hasActiveFilters = genre || bpmMin || bpmMax || camelotKey;

  return (
    <div className="discover-page">
      {/* Header */}
      <div className="discover-header">
        <h1>Discover</h1>
      </div>

      {/* Search Bar */}
      <div className="search-section">
        <div className="search-bar">
          <FontAwesomeIcon icon={faSearch} className="search-icon" />
          <input
            type="text"
            placeholder="Search by artist, title, genre..."
            value={query}
            onChange={handleQueryChange}
            autoFocus
          />
          {query && (
            <button
              className="clear-search"
              onClick={() => {
                setQuery("");
                doSearch({ genre, bpm_min: bpmMin, bpm_max: bpmMax, key: camelotKey });
              }}
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          )}
          <button
            className={`filter-toggle ${hasActiveFilters ? "active" : ""}`}
            onClick={() => setShowFilters(!showFilters)}
          >
            <FontAwesomeIcon icon={faFilter} />
            {hasActiveFilters && <span className="filter-dot" />}
          </button>
        </div>

        {/* Filter Panel */}
        {showFilters && (
          <div className="filter-panel">
            <div className="filter-row">
              <div className="filter-group">
                <label>Genre</label>
                <select value={genre} onChange={(e) => setGenre(e.target.value)}>
                  <option value="">All Genres</option>
                  {GENRES.map((g) => (
                    <option key={g} value={g}>{g}</option>
                  ))}
                </select>
              </div>

              <div className="filter-group bpm-group">
                <label>BPM</label>
                <div className="bpm-inputs">
                  <input
                    type="number"
                    placeholder="Min"
                    value={bpmMin}
                    onChange={(e) => setBpmMin(e.target.value)}
                    min="60"
                    max="200"
                  />
                  <span className="bpm-dash">-</span>
                  <input
                    type="number"
                    placeholder="Max"
                    value={bpmMax}
                    onChange={(e) => setBpmMax(e.target.value)}
                    min="60"
                    max="200"
                  />
                </div>
              </div>

              <div className="filter-group">
                <label>Key</label>
                <select value={camelotKey} onChange={(e) => setCamelotKey(e.target.value)}>
                  <option value="">Any Key</option>
                  {CAMELOT_KEYS.map((k) => (
                    <option key={k} value={k}>{k}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="filter-actions">
              <button className="apply-btn" onClick={handleFilterApply}>
                Apply Filters
              </button>
              {hasActiveFilters && (
                <button className="clear-btn" onClick={clearFilters}>
                  Clear All
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Results Meta */}
      {!loading && tracks.length > 0 && (
        <div className="results-meta">
          <span>
            {total} result{total !== 1 ? "s" : ""}
            {mode && <span className="mode-badge">{mode}</span>}
          </span>
          {searchTime && <span className="search-time">{Math.round(searchTime)}ms</span>}
        </div>
      )}

      {/* Track Grid */}
      <div className="track-grid">
        {loading ? (
          <div className="loading-state">
            <FontAwesomeIcon icon={faCompactDisc} spin className="loading-icon" />
            <p>Searching...</p>
          </div>
        ) : tracks.length === 0 ? (
          <div className="empty-state">
            <FontAwesomeIcon icon={faMusic} className="empty-icon" />
            <p>No tracks found. Try a different search.</p>
          </div>
        ) : (
          tracks.map((track) => (
            <TrackCard
              key={track.id}
              track={track}
              isPlaying={isPlaying && currentTrack?.id === track.id}
              onPlay={playTrack}
              onPause={() => {
                audioRef.current.pause();
                setIsPlaying(false);
              }}
              onFindSimilar={setRecTrack}
            />
          ))
        )}
      </div>

      {/* Preview Player Bar — portal to body so position:fixed escapes Framer Motion's filter */}
      {createPortal(
        <PreviewPlayer
          track={currentTrack}
          isPlaying={isPlaying}
          onPlayPause={togglePlayPause}
          audioRef={audioRef}
          onFindSimilar={setRecTrack}
        />,
        document.body
      )}

      {/* Recommendations Drawer */}
      <RecommendationsDrawer
        seedTrack={recTrack}
        isOpen={!!recTrack}
        onClose={() => setRecTrack(null)}
        onPlayTrack={playTrack}
        onPause={() => {
          audioRef.current.pause();
          setIsPlaying(false);
        }}
        currentTrack={currentTrack}
        isPlaying={isPlaying}
      />
    </div>
  );
};

export default Discover;
